/**
 * Chat visibility for one member — the one rule mobile and the backend share.
 *
 * Mobile's chat bubble (`computeCanSeeChat` in
 * `apps/mobile/src/context/hooks/chat/use-chat-permissions.ts`) and the backend's
 * chat unread email gate (SCH-2430, A2) both ask "is chat on for this person at
 * this school". They call this function so the two answers cannot drift. The
 * client-only PostHog `chat_mobile` flag is NOT part of it; mobile checks that
 * first, on its side.
 *
 * Chat is on for a member when all of these hold:
 *   - the school's `globalFeatures.chat.enabled` is set;
 *   - `betaOnly` is off, or the member is a beta tester;
 *   - `visibleForRoles` is absent, includes `'all'`, or names a role they hold;
 *   - the grade rule admits them (`isChatGradeEligible`, SCH-1288).
 */

import { isCommunityMember, type MenuRoleFlags } from './menu-roles'

/** The raw per-school role booleans a `visibleForRoles` label can match. */
type RoleFlags = MenuRoleFlags

/**
 * Role label → the raw role flags that satisfy it. `faculty` also accepts
 * `isTeacher`, so a teacher without an explicit `isFaculty` flag still matches a
 * faculty-scoped config. Module-private: callers depend on the function.
 */
const ROLE_FLAG_BY_LABEL: Readonly<Record<string, readonly (keyof RoleFlags)[]>> = {
  student: ['isStudent'],
  parent: ['isParent'],
  faculty: ['isFaculty', 'isTeacher'],
  staff: ['isStaff'],
  admin: ['isAdmin']
}

/** Whether one role label matches the member. `community` matches a member with no role flags. */
function labelMatches(roles: RoleFlags, label: string): boolean {
  if (label === 'community') return isCommunityMember(roles)
  return (ROLE_FLAG_BY_LABEL[label] ?? []).some(flag => roles[flag] === true)
}

/**
 * Whether the user holds at least one role named in a concrete `visibleForRoles`
 * list. Used by mobile's FAB visibility gate too, so every surface maps a role
 * label to the role booleans the same way. The label `community` matches a
 * member who holds none of the role flags. Callers own the universal cases (an
 * absent list or `['all']`).
 */
export function isRoleAllowedByLabel(roles: RoleFlags | null | undefined, visibleForRoles: readonly string[]): boolean {
  if (!roles) return false
  return visibleForRoles.some(label => labelMatches(roles, label))
}

/**
 * The member facts the rule reads, taken from their membership at the school in question.
 *
 * `gradeLevelPublicId` is a tri-state (KTD1): a string is the student's current
 * grade, `null` is "no grade on record", and `undefined` means the payload
 * predates the grade field and is treated as unrestricted. Only the mobile
 * adapter may pass `undefined`; server callers declare the field as a required
 * `string | null` so a forgotten grade load fails to typecheck instead of
 * admitting everyone. `roles.isStaff` must already be resolved by the caller.
 */
export interface ChatVisibilityMember {
  roles: RoleFlags | null | undefined
  isBetaTester: boolean
  gradeLevelPublicId?: string | null
}

/**
 * The base fields of `globalFeatures.chat` the rule reads. Widened on purpose: a
 * stored list may hold `'all'`, and a sparse override may omit a field, so a
 * parsed `ChatFeatureConfig` and a partial read both fit.
 */
export interface ChatVisibilityConfig {
  enabled?: boolean
  betaOnly?: boolean
  visibleForRoles?: readonly string[]
  studentGradeLevels?: readonly string[]
}

/** The member facts the grade rule alone reads. */
export type ChatGradeMember = Pick<ChatVisibilityMember, 'roles' | 'gradeLevelPublicId'>

/** Every role label except `student`: the roles that can keep a grade-excluded student in chat. */
const NON_STUDENT_LABELS = Object.keys(ROLE_FLAG_BY_LABEL).filter(label => label !== 'student')

/**
 * Whether the school's student grade list lets this member have chat
 * (SCH-1288, R4). It only ever narrows the student role:
 *   - no list, a non-student, or a grade of `undefined` → admitted;
 *   - a student whose grade is on the list → admitted;
 *   - otherwise (another grade, or `null`) → admitted only through another role
 *     the school shows chat to (`visibleForRoles` absent or `'all'` counts every
 *     role), e.g. a student worker flagged staff at a school that shows staff chat.
 *
 * This is the whole check for chat recipients and initiators on the server and
 * for the mobile candidate filter (KTD2): it ignores `enabled` and `betaOnly`,
 * so it never refuses someone whose chat is off for another reason.
 */
export function isChatGradeEligible(
  chatConfig: Pick<ChatVisibilityConfig, 'visibleForRoles' | 'studentGradeLevels'> | null | undefined,
  member: ChatGradeMember
): boolean {
  const grades = chatConfig?.studentGradeLevels
  if (!grades) return true
  if (member.roles?.isStudent !== true) return true
  if (member.gradeLevelPublicId === undefined) return true
  if (member.gradeLevelPublicId !== null && grades.includes(member.gradeLevelPublicId)) return true

  const visible = chatConfig?.visibleForRoles
  const otherLabels =
    !visible || visible.includes('all') ? NON_STUDENT_LABELS : visible.filter(label => label !== 'student')
  return isRoleAllowedByLabel(member.roles, otherLabels)
}

/** Whether chat is on for this member under the school's chat config. Fails closed on a missing config. */
export function isChatVisibleForMember(
  chatConfig: ChatVisibilityConfig | null | undefined,
  member: ChatVisibilityMember
): boolean {
  if (!chatConfig?.enabled) return false
  if (chatConfig.betaOnly && !member.isBetaTester) return false

  const roles = chatConfig.visibleForRoles
  if (roles && !roles.includes('all') && !isRoleAllowedByLabel(member.roles, roles)) return false
  return isChatGradeEligible(chatConfig, member)
}
